<template>
  <div>
    <form v-if="confirmUUID" @submit.prevent="authenticateWithPin">
      <div class="mb-3">
        <v-button
          text="word.back"
          size="sm"
          style-type="link"
          icon="chevron-left"
          @click="resetLoginConfirmation()"
        />
      </div>

      <div class="d-flex justify-content-center m-3">
        <i class="fa fa-lock text-cr-red"></i>
      </div>

      <div class="text-muted mb-3">
        {{ $t('sentence.login_request_mail_received') }}
        {{ $t('sentence.mail_in_spam') }}
      </div>
      <input
        required="required"
        id="register-pin-code"
        ref="pinCode"
        class="form-control form-control-lg text-big text-center mb-3"
        :placeholder="$t('sentence.fill_in_code')"
        pattern="[0-9.]+"
        maxlength="6"
        v-model="confirmPin"
      >
      <v-form-errors
        :errors="confirmErrors"
      />

      <v-button
        text="word.login"
        :block="true"
        type="submit"
        :loader="loader"
      />
    </form>
    <form
      method="post"
      @submit.prevent="requestPasswordReset"
      v-else-if="forgotPasswordMode"
    >
      <div class="mb-3">
        <v-button
          text="phrase.to_login"
          size="xs"
          style-type="secondary"
          icon="chevron-left"
          @click="forgotPasswordMode = false"
        />
      </div>
      <h4>
        {{ $t('phrase.reset_password') }}
      </h4>

      <div class="text-muted mb-3">
        {{ $t('sentence.request_password_reset') }}
      </div>
      <div class="mb-3">
        <v-text-input
          label="word.email"
          type="email"
          :auto-focus="true"
          :required="true"
          v-model="data.username"
          :errors="errors.username"
        />
      </div>

      <v-form-errors
        v-if="forgotPasswordError"
        :errors="[$t('sentence.email_address_not_found')]"
      />

      <v-button
        text="word.send"
        :block="true"
        type="submit"
        :loader="loader"
      />
    </form>
    <form
      method="post"
      @submit.prevent="authenticate"
      v-else
    >
      <div class="text-muted mb-3">
        {{ $t('sentence.login_explanation') }}
      </div>

      <div class="alert alert-info" v-if="forgotPasswordMessage">
        {{ $t('sentence.email_with_instructions') }}
      </div>

      <div class="mb-3">
        <v-text-input
          id="login-email"
          label="word.email"
          type="email"
          :auto-focus="true"
          :required="true"
          v-model="data.username"
          :errors="errors.username"
        />
      </div>

      <div class="mb-3">
        <v-text-input
          id="login-password"
          label="word.password"
          type="password"
          v-model="data.password"
          :errors="errors.password"
          :required="true"
        />

        <div class="d-flex justify-content-end" v-if="!forgotPasswordMode">
          <v-button
            id="button-forgot-password"
            text="phrase.forgot_password"
            size="xs"
            style-type="link"
            @click="forgotPasswordMode = true"
          />
        </div>
      </div>

      <v-form-errors
        :errors="errors.non_field_errors"
      />

      <v-button
        id="button-submit-login"
        text="word.login"
        :block="true"
        type="submit"
        :loader="loader"
      />
    </form>
  </div>
</template>

<script>
import Button from '@/common/components/Button.vue';
import FormErrors from '@/common/components/FormErrors.vue';
import TextInput from '@/common/components/inputs/TextInput.vue';
import api from '@/common/api';
import { mapActions } from 'vuex';

export default {
  name: 'LoginForm',
  components: {
    'v-button': Button,
    'v-form-errors': FormErrors,
    'v-text-input': TextInput,
  },
  data() {
    return {
      confirmUUID: false,
      confirmPin: null,
      confirmErrors: [],
      data: {
        username: '',
        password: '',
      },
      errors: { non_field_errors: [] },
      loader: { loading: false },
      forgotPasswordMessage: false,
      forgotPasswordError: false,
      forgotPasswordMode: false,
    };
  },
  created() {
    this.confirmUUID = sessionStorage.getItem('confirmUUID');

    if (this.$route.query.fpmode === 'true') {
      this.forgotPasswordMode = true;
    }
  },
  methods: {
    ...mapActions(['refreshUser']),
    resetLoginConfirmation() {
      sessionStorage.removeItem('confirmUUID');
      this.confirmUUID = null;
    },
    async authenticate() {
      this.loader.loading = true;
      this.data.tid = localStorage.getItem('tid');

      const response = await api.post('auth/token/', this.data);
      if (response.status === 400) {
        this.errors = response.data;
        this.loader.loading = false;
        return;
      }

      if (response.data.confirm_uuid) {
        sessionStorage.setItem('confirmUUID', response.data.confirm_uuid);
        this.confirmUUID = response.data.confirm_uuid;
        this.loader.loading = false;
      } else {
        this.$store.commit('login', response.data);
        sessionStorage.removeItem('registerEmail');
        await this.refreshUser();
        this.$emit('authenticated', response.data);
      }
    },
    async authenticateWithPin() {
      this.loader.loading = true;
      this.confirmErrors = [];
      const response = await api.post(
        `auth/login-requests/${this.confirmUUID}/token/`,
        { pin: this.confirmPin },
      );
      if (response.status === 400) {
        this.confirmErrors = response.data;
        this.loader.loading = false;
        return;
      }

      this.$store.commit('login', response.data);
      sessionStorage.removeItem('confirmUUID');
      await this.refreshUser();
      this.$emit('authenticated', response.data);
    },
    async requestPasswordReset() {
      this.loader.loading = true;
      this.forgotPasswordError = false;

      const response = await api.post(
        'auth/request-password-reset/',
        { email: this.data.username, dead_end_link: true },
      );
      if (response.status === 400) {
        this.forgotPasswordError = true;
        this.loader.loading = false;
        return;
      }

      this.forgotPasswordMessage = true;
      this.forgotPasswordMode = false;
      this.loader.loading = false;
    },
  },
};
</script>


<style scoped>
.fa-lock {
  font-size: 60px;
}

.form-control-lg {
  font-size: 30px !important;
  height: 80px;
  letter-spacing: 5px;
}
</style>
